import React, { ReactNode } from 'react';
import { OrderData } from '../../store/modules/order/types';
import QRCodeWithCopy from '../QRCodeWithCopy';
import CountdownBar from '../Countdown';
import { Container, ContainerChallenge, ContainerConfirmation } from './styles';
import useCheckPaymentPolling from '../../hooks/useCheckPaymentPolling';
import Loading from '../Loading';
import IconSuccess from '../../assets/icon-order-success.svg';
import IconFailed from '../../assets/icon-order-cancel-gray.svg';
import IconCancel from '../../assets/icon-order-cancel-red.svg';

interface PaymentConfirmationProps {
    order?: OrderData;
    isOrderPage?: boolean;
}

const getMessageByMediaAndStatus = (method: string, mediaType: string, status: string, isShippingStore: boolean) => {
    if (mediaType === 'pix') {
        const messagePixByStatus: Record<string, string | ReactNode> = {
            'approved': <>Pagamento via Pix <br /> confirmado com sucesso.</>,
            'failed': <>O pagamento via Pix não foi concluído <br /> dentro do prazo. Tente novamente.</>,
            'canceled': <>Pagamento pix foi <br /> cancelado e será estornado.</>
        }
        return messagePixByStatus[status] || messagePixByStatus.canceled;
    }

    if (method === 'cashondelivery') {
        const messageCashOnDeliveryByStatus: Record<string, string | ReactNode> = {
            'approved': <>Pedido confirmado! <br /> {isShippingStore ? 'Pagamento a ser realizado na retirada.' : 'Pagamento a ser realizado na entrega.'}</>,
            'canceled': 'Pedido cancelado.'
        }

        return messageCashOnDeliveryByStatus[status] || messageCashOnDeliveryByStatus.canceled
    }

    const messageDefaultByStatus: Record<string, string | ReactNode> = {
        'preapproved': <>Pagamento com cartão<br />confirmado com sucesso.</>,
        'approved': <>Pagamento com cartão<br />confirmado com sucesso.</>,
        'failed': <>O pagamento foi cancelado.<br />Tente novamente.</>,
        'canceled': <>Pagamento com cartão foi<br />cancelado e será estornado.</>
    }

    return messageDefaultByStatus[status] || messageDefaultByStatus.canceled;
}

const getIcon = (method: string, mediaType: string, status: string, isShippingStore: boolean) => {
    const iconDefaultByStatus: Record<string, { image: string, name: string}> = {
        'preapproved': {
            image: IconSuccess,
            name: 'Icone de sucesso'
        },
        'approved': {
            image: IconSuccess,
            name: 'Icone de sucesso'
        },
        'failed': {
            image: IconFailed,
            name: 'Icone de falha'
        },
        'canceled': {
            image: IconCancel,
            name: 'Icone de cancelamento'
        },
    } 

    return iconDefaultByStatus[status] || {
        image: IconFailed,
        name: 'Icone de falha'
    }
}

const PaymentConfirmation: React.FC<PaymentConfirmationProps> = ({
    order,
    isOrderPage = false
}: PaymentConfirmationProps) => {
    useCheckPaymentPolling({
        order,
        isOrderPage,
    });

    if (!order
        || !order.payment_detail
        || (order.payment_detail && order.payment_detail.length < 1)
    )
        return <p>Pagamento na entrega</p>;

    if (order.payment_detail[0].status === 'pending' && order.payment_detail[0].media_type === 'pix') {
        return (
            <Container>
                <p>Use o QR Code abaixo para realizar o pagamento via Pix.</p>
                <QRCodeWithCopy value={order.payment_detail[0].additional.qr_code as string} />
                <CountdownBar expiration={order.payment_detail[0].additional.expiration as string} start={order?.created_at} />
            </Container>
        )
    }

    if (order.payment_detail[0].status === 'pending' && order.payment_detail[0].media_type === 'debit') {
        return (
            <Container>
                <p>Estamos processando seu pagamento no cartão de débito. <br /> Caso o banco solicite, pediremos uma confirmação extra. Aguarde um instante.</p>
                <Loading size={150} />
                {
                    order.payment_detail[0].additional.ds_data && order.payment_detail[0].additional.ds_data.threeDSMethodURL && (
                        <iframe
                            name="threeDSMethodFrame"
                            title="3DS Method"
                            srcDoc={`
            <form id="methodForm" action="${order.payment_detail[0].additional.ds_data.threeDSMethodURL}" method="POST">
              <input type="hidden" name="threeDSMethodData" value="${order.payment_detail[0].additional.ds_data.threeDSMethodData}" />
            </form>
            <script>document.getElementById('methodForm').submit();</script>
          `}
                            style={{ display: "none" }}
                        />
                    )
                }
            </Container>
        )
    }


    if (order.payment_detail[0].status === '3dssecure' && order.payment_detail[0].media_type === 'debit') {
        return (
            <>
                {order.payment_detail[0].additional.challenge_data && order.payment_detail[0].additional.challenge_data.url && (
                    <ContainerChallenge>
                        <p>Seu banco solicitou uma confirmação extra de segurança. Siga as instruções abaixo para finalizar o pagamento.</p>
                        <iframe
                            name="challengeFrame"
                            title="3DS Challenge"
                            style={{ border: "none" }}
                            srcDoc={`
            <form id="challengeForm" action="${order.payment_detail[0].additional.challenge_data.url}" method="POST" target="_self">
            <input type="hidden" name="creq" value="${order.payment_detail[0].additional.challenge_data.data}" />
            </form>
            <script>document.getElementById('challengeForm').submit();</script>
        `}
                        />
                    </ContainerChallenge>
                )}
            </>
        )
    }

    const icon = getIcon(order.payment_detail[0].method as string, order.payment_detail[0].media_type, order.payment_detail[0].status as string, !!(order?.shipping_method === 'store'));

    return (
        <ContainerConfirmation>
            <img src={icon.image} alt={icon.name} />
            <p>{getMessageByMediaAndStatus(order.payment_detail[0].method as string, order.payment_detail[0].media_type, order.payment_detail[0].status as string, !!(order.shipping_method === 'store'))}</p>
            {order.payment_detail[0].method !== 'cashondelivery' && <p>Código da transação: {order.payment_detail[0].tef_nita}</p>}
        </ContainerConfirmation>
    )

}

export default PaymentConfirmation;